import { useCustomer, useOperationFile, useOperationVersions, useUser, useUsers } from '@/core/api';
import { useIntl } from '@/core/i18n/use-intl';
import {
  ActivityBody,
  ActivityFieldChange,
  ActivityHeader,
  ActivityNote,
} from '@/shared/components/activity';
import { ActivityTimeline } from '@/shared/components/activity/activity-timeline';

type FileUploadedChange = {
  kind?: 'report' | 'followup-protocol' | 'attachment';
  filename?: string | null;
};

const userLabel = (user?: {
  id?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}) => {
  if (!user) return null;
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.email || null;
};

const isUserAssignmentArrayField = (field: string) =>
  field === 'heliProviderUsers' || field === 'dispatcherUsers';

const normalizeRelationshipIds = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (typeof item === 'string' || typeof item === 'number') {
        return String(item).trim();
      }
      if (item && typeof item === 'object' && 'id' in item) {
        const id = (item as { id?: unknown }).id;
        return typeof id === 'string' || typeof id === 'number' ? String(id).trim() : '';
      }
      return '';
    })
    .filter(Boolean);
};

export function OperationActivity({ operationId }: { operationId: string }) {
  const { t } = useIntl();

  const {
    data: activityData,
    isLoading: isLoadingActivities,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useOperationVersions(operationId, { disabled: !operationId, limit: 15 });

  if (isLoadingActivities) {
    return <div className="text-center text-muted-foreground">{t('activity.loading')}</div>;
  }

  if (!activityData?.pages[0].docs.length) {
    return <div className="text-center text-muted-foreground">{t('activity.noActivity')}</div>;
  }

  const allActivities = activityData.pages.flatMap((page) => page.docs);
  if (!allActivities.length) {
    return <div className="text-center text-muted-foreground">{t('activity.noActivity')}</div>;
  }

  const filteredActivities = allActivities.filter((activity) => {
    if (activity.type === 'created') return !hasNextPage;
    return true;
  });

  if (!filteredActivities.length) {
    return <div className="text-center text-muted-foreground">{t('activity.noActivity')}</div>;
  }

  const getFieldName = (field: string) => {
    const key = `activity.operation.fields.${field}` as unknown as never;
    const translated = t(key) as unknown as string;
    return translated || field;
  };

  return (
    <div className="space-y-4">
      <ActivityTimeline
        activities={filteredActivities.map((activity) => ({
          id: activity.id,
          type: activity.type as 'created' | 'updated',
          timestamp: activity.timestamp,
          user: activity.user,
          changes: activity.changes,
        }))}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
      >
        {(activity) => {
          if (activity.type === 'created') {
            return (
              <ActivityHeader userId={activity.user} timestamp={activity.timestamp}>
                <span className="text-muted-foreground">{t('activity.operation.created')}</span>
              </ActivityHeader>
            );
          }

          const nodes = (activity.changes ?? []).map((change, index) => {
            if (change.field === 'fileUploaded') {
              const payload = (change.to || {}) as FileUploadedChange;
              const kind = payload.kind || 'attachment';
              const kindLabelKey = `activity.operation.fileKinds.${kind}` as unknown as never;
              const kindLabel = (t(kindLabelKey) as unknown as string) || kind;
              const fileLabel = payload.filename ? `${kindLabel}: ${payload.filename}` : kindLabel;

              return (
                <ActivityNote
                  key={index}
                  action={t('activity.operation.fileUploaded')}
                  subject={fileLabel}
                />
              );
            }

            return (
              <ActivityFieldChange
                key={index}
                label={getFieldName(change.field)}
                from={<ResolvedOperationValue field={change.field} value={change.from} />}
                to={<ResolvedOperationValue field={change.field} value={change.to} />}
              />
            );
          });

          return (
            <div>
              <ActivityHeader userId={activity.user} timestamp={activity.timestamp} />
              {nodes.length > 0 && <ActivityBody>{nodes}</ActivityBody>}
            </div>
          );
        }}
      </ActivityTimeline>
    </div>
  );
}

function ResolvedOperationValue({ field, value }: { field: string; value: unknown }) {
  const { t } = useIntl();
  const stringValue =
    typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
  const relationshipIds = isUserAssignmentArrayField(field) ? normalizeRelationshipIds(value) : [];

  const { data: customer } = useCustomer(field === 'customer' ? stringValue : '');
  const { data: user } = useUser(stringValue, {
    disabled: !stringValue || (field !== 'responsibleGmnUser' && field !== 'assignedCustomerUser'),
  });
  const { data: operationFile } = useOperationFile(stringValue, {
    disabled: !stringValue || field !== 'reportFile',
  });
  const { data: assignedUsers } = useUsers({
    disabled: relationshipIds.length === 0,
    limit: 0,
    where: { id: { in: relationshipIds } },
  });

  if (value === null || value === undefined || value === '')
    return <span>{t('activity.none')}</span>;

  if (typeof value === 'string' || typeof value === 'number') {
    if (field === 'status') {
      return <span>{t(`operations.status.${stringValue}` as never) || stringValue}</span>;
    }

    if (field === 'followUpDecision') {
      return <span>{t(`operations.followUpDecision.${stringValue}` as never) || stringValue}</span>;
    }

    if (field === 'customer') {
      return <span>{customer?.name || stringValue}</span>;
    }

    if (field === 'responsibleGmnUser' || field === 'assignedCustomerUser') {
      return <span>{userLabel(user) || stringValue}</span>;
    }

    if (field === 'reportFile') {
      return <span>{operationFile?.filename || stringValue}</span>;
    }

    return <span>{stringValue}</span>;
  }

  if (typeof value === 'boolean') {
    return <span>{value ? t('common.yes') : t('common.no')}</span>;
  }

  if (Array.isArray(value)) {
    if (!isUserAssignmentArrayField(field)) {
      return <span>{value.length > 0 ? value.join(', ') : t('activity.none')}</span>;
    }

    if (relationshipIds.length === 0) {
      return <span>{t('activity.none')}</span>;
    }

    const usersById = new Map((assignedUsers?.docs ?? []).map((item) => [item.id, item]));
    const labels = relationshipIds.map((id) => {
      const inlineValue = value.find((item) => {
        if (typeof item === 'string' || typeof item === 'number') return String(item) === id;
        return item && typeof item === 'object' && 'id' in item && String(item.id) === id;
      });
      const inlineLabel =
        inlineValue && typeof inlineValue === 'object'
          ? userLabel(inlineValue as Parameters<typeof userLabel>[0])
          : null;
      return inlineLabel || userLabel(usersById.get(id)) || id;
    });

    return <span>{labels.join(', ')}</span>;
  }

  return <span>{t('activity.none')}</span>;
}
